import { useEffect, useState } from "react";
import PortalLayout from "@/layouts/PortalLayout";
import { api } from "@/lib/api";
import { poleLabel } from "@/lib/poles";
import { toast } from "sonner";
import {
  TrendingUp, TrendingDown, Users, Calendar, Megaphone, Tag, Target, BarChart3,
  Mail, FileText, Clock, Euro, GraduationCap, Sparkles, Pencil, Check, X, UserPlus, UserMinus,
  Handshake, RefreshCw, Repeat,
} from "lucide-react";

const SECTION_COLOR = {
  revenue: "from-amber-50 to-white",
  members_evo: "from-emerald-50 to-white",
  acquisition: "from-wine-50 to-white",
  events: "from-emerald-50 to-white",
  contents: "from-indigo-50 to-white",
  ecosystem: "from-blush-50 to-white",
};

function Kpi({ icon: Icon, label, value, hint, accent = "wine", testId }) {
  const accents = {
    wine: "bg-wine-50 text-wine-700",
    emerald: "bg-emerald-50 text-emerald-700",
    indigo: "bg-indigo-50 text-indigo-700",
    amber: "bg-amber-50 text-amber-700",
    blush: "bg-blush-50 text-blush-700",
  };
  return (
    <div className="rounded-2xl border border-neutral-100 bg-white p-5">
      <div className={`grid h-10 w-10 place-items-center rounded-xl ${accents[accent] || accents.wine}`}>
        <Icon className="h-5 w-5" />
      </div>
      <div
        className="mt-3 font-serif text-3xl font-semibold text-neutral-900"
        data-testid={testId || `kpi-${String(label).replace(/\s+/g, "-").toLowerCase()}`}
      >
        {value}
      </div>
      <div className="text-sm font-medium text-neutral-700">{label}</div>
      {hint && <div className="mt-1 text-xs text-neutral-500">{hint}</div>}
    </div>
  );
}

function Bars({ data, keyName = "_id", labelKey = "label", valueKey = "count", color = "wine" }) {
  const total = data.reduce((s, d) => s + (d[valueKey] || 0), 0) || 1;
  const bar = {
    wine: "from-wine-400 to-wine-600",
    emerald: "from-emerald-400 to-emerald-600",
    indigo: "from-indigo-400 to-indigo-600",
    amber: "from-amber-400 to-amber-600",
  };
  return (
    <div className="space-y-2">
      {data.map((d) => {
        const pct = Math.round(((d[valueKey] || 0) / total) * 100);
        const label = d[labelKey] || d[keyName] || "—";
        return (
          <div key={String(label)}>
            <div className="flex items-center justify-between text-xs">
              <span className="text-neutral-700">{label}</span>
              <span className="font-semibold text-neutral-700">
                {d[valueKey]} <span className="text-neutral-400">({pct}%)</span>
              </span>
            </div>
            <div className="mt-1 h-1.5 overflow-hidden rounded-full bg-neutral-100">
              <div className={`h-full rounded-full bg-gradient-to-r ${bar[color] || bar.wine}`} style={{ width: `${pct}%` }} />
            </div>
          </div>
        );
      })}
      {data.length === 0 && (
        <div className="rounded-lg bg-neutral-50 p-4 text-center text-xs text-neutral-400">Aucune donnée</div>
      )}
    </div>
  );
}

function formatEUR(amount) {
  return new Intl.NumberFormat("fr-FR", {
    style: "currency",
    currency: "EUR",
    maximumFractionDigits: 0,
  }).format(amount || 0);
}

const REV_META = {
  adhesion:  {
    icon: Users,
    iconClass: "bg-wine-50 text-wine-700",
    barClass: "from-wine-400 to-wine-600",
  },
  event:     {
    icon: Calendar,
    iconClass: "bg-emerald-50 text-emerald-700",
    barClass: "from-emerald-400 to-emerald-600",
  },
  formation: {
    icon: GraduationCap,
    iconClass: "bg-indigo-50 text-indigo-700",
    barClass: "from-indigo-400 to-indigo-600",
  },
};

function RevenueSection({ revenue, onTargetSaved }) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");
  const [saving, setSaving] = useState(false);

  if (!revenue) return null;
  const total = revenue.total || 0;
  const total30 = revenue.total_30d || 0;
  const yearTotal = revenue.year_total || 0;
  const target = revenue.target_annual || 0;
  const pct = target > 0 ? Math.min(100, Math.round((yearTotal / target) * 1000) / 10) : 0;
  const onTrack = target > 0 && pct >= (new Date().getMonth() + 1) * (100 / 12) - 5;

  const openEdit = () => {
    setDraft(target > 0 ? String(target) : "");
    setEditing(true);
  };
  const cancelEdit = () => setEditing(false);
  const saveTarget = async () => {
    const num = parseFloat(String(draft).replace(",", "."));
    if (Number.isNaN(num) || num < 0) {
      toast.error("Montant invalide");
      return;
    }
    try {
      setSaving(true);
      await api.patch("/admin/settings/revenue-target", { target_annual: num });
      toast.success("Objectif annuel mis à jour");
      setEditing(false);
      onTargetSaved?.();
    } catch (err) {
      toast.error(err.response?.data?.detail || "Erreur lors de la sauvegarde");
    } finally {
      setSaving(false);
    }
  };

  return (
    <section
      className={`rounded-3xl bg-gradient-to-b ${SECTION_COLOR.revenue} p-6`}
      data-testid="section-revenue"
    >
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
            <Euro className="h-5 w-5 text-amber-600" /> Chiffre d&apos;affaires
          </h2>
          <p className="mt-0.5 text-xs text-neutral-500">
            Total cumulé · Adhésions, Événements et Formations payés (Stripe).
          </p>
        </div>
        <div className="text-right">
          <div className="font-serif text-3xl font-semibold text-amber-700" data-testid="kpi-ca-total">
            {formatEUR(total)}
          </div>
          <div className="text-xs text-neutral-500">
            dont {formatEUR(total30)} sur 30 jours
          </div>
        </div>
      </div>

      {/* Annual target + progress gauge */}
      <div
        className="mt-5 rounded-2xl border border-amber-200 bg-white p-5"
        data-testid="revenue-target-block"
      >
        <div className="flex flex-wrap items-center justify-between gap-3">
          <div className="inline-flex items-center gap-2">
            <Target className="h-4 w-4 text-amber-600" />
            <span className="text-sm font-bold text-neutral-700">
              Objectif {revenue.year || new Date().getFullYear()}
            </span>
          </div>
          {!editing ? (
            <div className="inline-flex items-center gap-3">
              <span className="font-serif text-xl font-semibold text-neutral-900" data-testid="revenue-target-value">
                {target > 0 ? formatEUR(target) : "Non défini"}
              </span>
              <button
                type="button"
                onClick={openEdit}
                className="inline-flex items-center gap-1 rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-xs font-semibold text-amber-700 hover:bg-amber-100"
                data-testid="revenue-target-edit-btn"
              >
                <Pencil className="h-3 w-3" /> {target > 0 ? "Modifier" : "Définir"}
              </button>
            </div>
          ) : (
            <div className="inline-flex items-center gap-2">
              <input
                type="number"
                min="0"
                step="100"
                value={draft}
                onChange={(e) => setDraft(e.target.value)}
                placeholder="ex. 80000"
                className="w-32 rounded-md border border-neutral-200 px-2 py-1 text-sm focus:border-amber-400 focus:outline-none focus:ring-1 focus:ring-amber-200"
                data-testid="revenue-target-input"
                autoFocus
              />
              <span className="text-xs text-neutral-500">€ / an</span>
              <button
                type="button"
                onClick={saveTarget}
                disabled={saving}
                className="rounded-full bg-amber-600 p-1.5 text-white hover:bg-amber-700 disabled:opacity-50"
                data-testid="revenue-target-save-btn"
              >
                <Check className="h-3.5 w-3.5" />
              </button>
              <button
                type="button"
                onClick={cancelEdit}
                className="rounded-full bg-neutral-100 p-1.5 text-neutral-600 hover:bg-neutral-200"
                data-testid="revenue-target-cancel-btn"
              >
                <X className="h-3.5 w-3.5" />
              </button>
            </div>
          )}
        </div>

        {target > 0 && (
          <div className="mt-4">
            <div className="flex items-baseline justify-between text-xs text-neutral-600">
              <span>
                <span className="font-bold text-amber-700">{formatEUR(yearTotal)}</span> réalisés ·{" "}
                <span className="text-neutral-500">il reste {formatEUR(Math.max(0, target - yearTotal))}</span>
              </span>
              <span
                className={`font-bold ${onTrack ? "text-emerald-700" : pct >= 100 ? "text-emerald-700" : "text-wine-700"}`}
                data-testid="revenue-target-pct"
              >
                {pct}%
              </span>
            </div>
            <div className="mt-2 h-3 overflow-hidden rounded-full bg-amber-100">
              <div
                className={`h-full rounded-full transition-all duration-700 ${
                  pct >= 100
                    ? "bg-gradient-to-r from-emerald-400 to-emerald-600"
                    : onTrack
                      ? "bg-gradient-to-r from-amber-400 to-emerald-500"
                      : "bg-gradient-to-r from-amber-400 to-amber-600"
                }`}
                style={{ width: `${Math.min(100, pct)}%` }}
              />
            </div>
            <p className="mt-2 text-[11px] text-neutral-500">
              {pct >= 100
                ? "🎉 Objectif annuel atteint, bravo !"
                : onTrack
                  ? "✅ Vous êtes sur le rythme attendu pour cette période de l'année."
                  : "⚠️ Vous êtes en retard sur le rythme attendu — pensez à booster vos campagnes."}
            </p>
          </div>
        )}
        {target === 0 && (
          <p className="mt-3 text-xs text-neutral-500">
            Aucun objectif annuel défini pour le moment. Cliquez sur « Définir » pour fixer une cible.
          </p>
        )}
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-3">
        {revenue.breakdown.map((row) => {
          const meta = REV_META[row.key] || REV_META.adhesion;
          const breakdownPct = total > 0 ? Math.round((row.amount / total) * 100) : 0;
          return (
            <div
              key={row.key}
              className="rounded-2xl border border-neutral-100 bg-white p-5"
              data-testid={`ca-${row.key}`}
            >
              <div className="flex items-center justify-between">
                <div className={`grid h-10 w-10 place-items-center rounded-xl ${meta.iconClass}`}>
                  <meta.icon className="h-5 w-5" />
                </div>
                <span className="text-xs font-semibold text-neutral-400">{breakdownPct}%</span>
              </div>
              <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">
                {formatEUR(row.amount)}
              </div>
              <div className="text-sm font-medium text-neutral-700">{row.label}</div>
              <div className="mt-1 text-xs text-neutral-500">
                {row.count} transaction{row.count > 1 ? "s" : ""} · {formatEUR(row.amount_30d)} sur 30j
              </div>
              <div className="mt-3 h-1.5 overflow-hidden rounded-full bg-neutral-100">
                <div
                  className={`h-full rounded-full bg-gradient-to-r ${meta.barClass}`}
                  style={{ width: `${breakdownPct}%` }}
                />
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}


function SharedServicesRevenueSection() {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const load = (refresh = false) => {
    setLoading(true);
    api.get(`/admin/analytics/shared-services-revenue${refresh ? "?refresh=true" : ""}`)
      .then((r) => setData(r.data))
      .catch(() => toast.error("Revenus services : chargement impossible"))
      .finally(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);
  const t = data?.totals || {};
  return (
    <section className="rounded-3xl bg-gradient-to-b from-blush-50 to-white p-6" data-testid="section-services-revenue">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
          <Handshake className="h-5 w-5 text-wine-600" /> Revenus services mutualisés
        </h2>
        <button onClick={() => load(true)} disabled={loading} className="inline-flex items-center gap-1.5 rounded-full border border-neutral-200 bg-white px-3 py-1.5 text-xs font-semibold text-navy hover:bg-neutral-50 disabled:opacity-60" data-testid="services-revenue-refresh">
          <RefreshCw className={`h-3.5 w-3.5 ${loading ? "animate-spin" : ""}`} /> Actualiser depuis Stripe
        </button>
      </div>
      {data?.error && <p className="mt-3 rounded-xl bg-amber-50 px-3 py-2 text-xs text-amber-800" data-testid="services-revenue-error">Stripe indisponible : {data.error}</p>}
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={Repeat} label="Revenu récurrent mensuel" value={formatEUR(t.mrr)} hint={`${t.subscribers || 0} abonnement(s) actif(s)`} accent="blush" testId="services-kpi-mrr" />
        <Kpi icon={TrendingUp} label="Revenu annualisé" value={formatEUR(t.arr)} hint="revenu mensuel × 12" accent="blush" testId="services-kpi-arr" />
        <Kpi icon={Euro} label="Encaissé ce mois-ci" value={formatEUR(t.collected_month)} hint="factures payées" accent="blush" testId="services-kpi-month" />
        <Kpi icon={Calendar} label="Encaissé sur 12 mois" value={formatEUR(t.collected_12m)} hint="abonnements + ponctuels" accent="blush" testId="services-kpi-12m" />
      </div>
      <div className="mt-5 overflow-x-auto rounded-2xl border border-neutral-100 bg-white">
        <table className="w-full min-w-[640px] text-sm" data-testid="services-revenue-table">
          <thead className="bg-neutral-50 text-left text-[10px] uppercase tracking-wider text-neutral-500">
            <tr><th className="px-4 py-2">Service</th><th className="px-4 py-2 text-right">Abonnés actifs</th><th className="px-4 py-2 text-right">Revenu mensuel récurrent</th><th className="px-4 py-2 text-right">Encaissé 12 mois</th><th className="px-4 py-2 text-right">Ventes ponctuelles</th></tr>
          </thead>
          <tbody>
            {(data?.services || []).map((r) => (
              <tr key={r.service_id} className="border-t border-neutral-100" data-testid={`services-revenue-row-${r.service_id}`}>
                <td className="px-4 py-2.5 font-semibold text-navy">{r.title}{!r.stripe_linked && <span className="ml-2 text-[10px] font-normal text-amber-700">non synchronisé avec Stripe</span>}</td>
                <td className="px-4 py-2.5 text-right">{r.subscribers}</td>
                <td className="px-4 py-2.5 text-right font-semibold">{formatEUR(r.mrr)}</td>
                <td className="px-4 py-2.5 text-right">{formatEUR(r.collected_12m)}</td>
                <td className="px-4 py-2.5 text-right">{r.one_time_count} <span className="text-xs text-neutral-500">({formatEUR(r.one_time_amount)})</span></td>
              </tr>
            ))}
            {!loading && !(data?.services || []).length && (
              <tr><td colSpan={5} className="px-4 py-8 text-center text-sm text-neutral-500" data-testid="services-revenue-empty">Aucun service mutualisé avec un prix facturable pour l'instant.</td></tr>
            )}
          </tbody>
        </table>
      </div>
      {data?.computed_at && <p className="mt-2 text-[10px] text-neutral-400">Calculé le {new Date(data.computed_at).toLocaleString("fr-FR")} · montants finaux en EUR</p>}
    </section>
  );
}

function MembersEvolutionSection({ data }) {
  if (!data) return null;
  const rows = data.rows || [];
  const summary = data.summary || {};
  // Find max for chart scaling — include both new and churn
  const maxVal = Math.max(1, ...rows.map((r) => Math.max(r.new_members, r.churn)));
  return (
    <section
      className={`rounded-3xl bg-gradient-to-b ${SECTION_COLOR.members_evo} p-6`}
      data-testid="section-members-evolution"
    >
      <div className="flex items-end justify-between gap-4">
        <div>
          <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
            <TrendingUp className="h-5 w-5 text-emerald-600" /> Évolution des membres
          </h2>
          <p className="mt-0.5 text-xs text-neutral-500">
            12 derniers mois · entrées (adhésions Stripe payées) · churn (renouvellements expirés ou refusés)
          </p>
        </div>
        <div className="text-right">
          <div className="font-serif text-3xl font-semibold text-emerald-700" data-testid="kpi-members-active-now">
            {summary.active_now}
          </div>
          <div className="text-xs text-neutral-500">membres actifs aujourd&apos;hui</div>
        </div>
      </div>

      {/* Summary KPIs */}
      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid="kpi-members-new">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-emerald-50 text-emerald-700">
            <UserPlus className="h-5 w-5" />
          </div>
          <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">+{summary.total_new}</div>
          <div className="text-sm font-medium text-neutral-700">Nouveaux membres</div>
          <div className="mt-1 text-xs text-neutral-500">sur les 12 derniers mois</div>
        </div>
        <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid="kpi-members-churn">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-rose-50 text-rose-700">
            <UserMinus className="h-5 w-5" />
          </div>
          <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">−{summary.total_churn}</div>
          <div className="text-sm font-medium text-neutral-700">Churn</div>
          <div className="mt-1 text-xs text-neutral-500">non-renouvellements</div>
        </div>
        <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid="kpi-members-net">
          <div className={`grid h-10 w-10 place-items-center rounded-xl ${summary.net >= 0 ? "bg-emerald-50 text-emerald-700" : "bg-rose-50 text-rose-700"}`}>
            {summary.net >= 0 ? <TrendingUp className="h-5 w-5" /> : <TrendingDown className="h-5 w-5" />}
          </div>
          <div className={`mt-3 font-serif text-3xl font-semibold ${summary.net >= 0 ? "text-emerald-700" : "text-rose-700"}`}>
            {summary.net >= 0 ? "+" : ""}{summary.net}
          </div>
          <div className="text-sm font-medium text-neutral-700">Solde net</div>
          <div className="mt-1 text-xs text-neutral-500">entrées − sorties</div>
        </div>
        <div className="rounded-2xl border border-neutral-100 bg-white p-5" data-testid="kpi-members-churn-rate">
          <div className="grid h-10 w-10 place-items-center rounded-xl bg-amber-50 text-amber-700">
            <Target className="h-5 w-5" />
          </div>
          <div className="mt-3 font-serif text-3xl font-semibold text-neutral-900">{summary.avg_churn_rate_pct}%</div>
          <div className="text-sm font-medium text-neutral-700">Taux de churn</div>
          <div className="mt-1 text-xs text-neutral-500">
            {summary.best_month ? `Meilleur mois : ${summary.best_month} (+${summary.best_month_count})` : "Aucune donnée"}
          </div>
        </div>
      </div>

      {/* Monthly bars chart */}
      <div className="mt-5 rounded-2xl border border-neutral-100 bg-white p-5">
        <h3 className="inline-flex items-center gap-2 text-sm font-bold text-neutral-700">
          <BarChart3 className="h-4 w-4 text-emerald-600" /> Mensuel — entrées vs churn
        </h3>
        <div className="mt-5 grid grid-cols-12 items-end gap-1.5" style={{ minHeight: 200 }} data-testid="members-evolution-chart">
          {rows.map((r) => {
            const newH = Math.round((r.new_members / maxVal) * 160);
            const churnH = Math.round((r.churn / maxVal) * 160);
            return (
              <div key={`${r.year}-${r.month}`} className="flex flex-col items-center gap-1">
                <div className="flex h-[170px] w-full items-end justify-center gap-0.5">
                  {/* New bar */}
                  <div
                    className="w-2.5 rounded-t bg-gradient-to-t from-emerald-500 to-emerald-300 transition-all"
                    style={{ height: `${newH}px` }}
                    title={`+${r.new_members} entrée${r.new_members > 1 ? "s" : ""} en ${r.label}`}
                    data-testid={`bar-new-${r.year}-${r.month}`}
                  />
                  {/* Churn bar */}
                  <div
                    className="w-2.5 rounded-t bg-gradient-to-t from-rose-500 to-rose-300 transition-all"
                    style={{ height: `${churnH}px` }}
                    title={`−${r.churn} churn en ${r.label}`}
                    data-testid={`bar-churn-${r.year}-${r.month}`}
                  />
                </div>
                <span className="text-[10px] font-medium uppercase tracking-wide text-neutral-500">{r.label}</span>
              </div>
            );
          })}
        </div>
        {/* Legend */}
        <div className="mt-5 flex items-center justify-center gap-6 text-xs">
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm bg-gradient-to-t from-emerald-500 to-emerald-300" />
            <span className="text-neutral-700">Nouvelles adhésions</span>
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span className="h-3 w-3 rounded-sm bg-gradient-to-t from-rose-500 to-rose-300" />
            <span className="text-neutral-700">Churn (non-renouvelés)</span>
          </span>
        </div>
      </div>
    </section>
  );
}

function AudreyCommissionSection() {
  const now = new Date();
  const defaultMonth = `${now.getFullYear()}-${String(now.getMonth() + 1).padStart(2, "0")}`;
  const [month, setMonth] = useState(defaultMonth);
  const [recipients, setRecipients] = useState([]);
  const [recipientId, setRecipientId] = useState("audrey");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [exporting, setExporting] = useState(false);

  useEffect(() => {
    api
      .get("/admin/analytics/commission-recipients")
      .then((r) => {
        setRecipients(r.data || []);
        if (r.data?.length && !r.data.find((x) => x.id === recipientId)) {
          setRecipientId(r.data[0].id);
        }
      })
      .catch(() => {});
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const load = (m, rid) => {
    setLoading(true);
    api
      .get("/admin/analytics/commissions", { params: { month: m, recipient: rid } })
      .then((r) => setData(r.data))
      .catch(() => toast.error("Impossible de charger les commissions"))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load(month, recipientId);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [month, recipientId]);

  const exportPdf = async () => {
    try {
      setExporting(true);
      const resp = await api.get("/admin/analytics/commissions/export.pdf", {
        params: { month, recipient: recipientId },
        responseType: "blob",
      });
      const blob = new Blob([resp.data], { type: "application/pdf" });
      const url = URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `commission-${recipientId}-${month}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success("PDF exporté");
    } catch {
      toast.error("Export PDF impossible");
    } finally {
      setExporting(false);
    }
  };

  const fmtEur = (v) =>
    new Intl.NumberFormat("fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: 2 }).format(v || 0);
  const fmtDate = (iso) => {
    if (!iso) return "—";
    try {
      return new Date(iso).toLocaleDateString("fr-FR");
    } catch {
      return "—";
    }
  };

  const items = data?.items || [];
  const totals = data?.totals || { new_count: 0, renewal_count: 0, ttc_total: 0, commission_total: 0 };
  const current = recipients.find((r) => r.id === recipientId);

  return (
    <section
      className="rounded-3xl bg-gradient-to-b from-rose-50 to-white p-6"
      data-testid="section-audrey-commission"
    >
      <div className="flex flex-wrap items-end justify-between gap-4">
        <div>
          <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
            <Euro className="h-5 w-5 text-wine-600" /> Commissions commerciales
          </h2>
          <p className="mt-1 text-xs text-neutral-500">
            {current
              ? `${current.new_rate_pct}% TTC sur chaque nouvelle adhésion ${current.scope_label} · ${current.renewal_rate_pct}% TTC sur les renouvellements. Source : factures Stripe payées.`
              : "Chargement du périmètre…"}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-2">
          <select
            value={recipientId}
            onChange={(e) => setRecipientId(e.target.value)}
            className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm"
            data-testid="commission-recipient-picker"
          >
            {recipients.map((r) => (
              <option key={r.id} value={r.id}>
                {r.name} — {r.scope_label}
              </option>
            ))}
          </select>
          <input
            type="month"
            value={month}
            onChange={(e) => setMonth(e.target.value)}
            max={defaultMonth}
            className="rounded-xl border border-neutral-200 bg-white px-3 py-2 text-sm"
            data-testid="commission-month-picker"
          />
          <button
            onClick={exportPdf}
            disabled={exporting || loading}
            className="inline-flex items-center gap-1.5 rounded-xl bg-wine-500 px-4 py-2 text-xs font-semibold text-white hover:bg-wine-600 disabled:opacity-50"
            data-testid="commission-export-pdf"
          >
            <FileText className="h-3.5 w-3.5" />
            {exporting ? "Export…" : "Exporter PDF"}
          </button>
        </div>
      </div>

      <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
        <Kpi icon={UserPlus} label="Nouvelles adhésions" value={totals.new_count} hint={`× ${current?.new_rate_pct ?? 15}% TTC`} accent="emerald" testId="kpi-commission-new" />
        <Kpi icon={TrendingUp} label="Renouvellements" value={totals.renewal_count} hint={`× ${current?.renewal_rate_pct ?? 5}% TTC`} accent="amber" testId="kpi-commission-renewal" />
        <Kpi icon={Euro} label={`CA ${current?.scope_label || ""} TTC`} value={fmtEur(totals.ttc_total)} hint="du mois" accent="indigo" testId="kpi-commission-ttc" />
        <Kpi icon={Sparkles} label="Commission" value={fmtEur(totals.commission_total)} hint="à verser" accent="wine" testId="kpi-commission-total" />
      </div>

      <div className="mt-6 overflow-hidden rounded-2xl border border-wine-100 bg-white" data-testid="commission-table">
        {loading ? (
          <div className="p-8 text-center text-sm text-neutral-500">Chargement…</div>
        ) : items.length === 0 ? (
          <div className="p-8 text-center text-sm italic text-neutral-500">
            Aucune adhésion {current?.scope_label?.toLowerCase() || ""} facturée sur {month}.
          </div>
        ) : (
          <table className="w-full text-sm">
            <thead className="bg-rose-50/60 text-[11px] uppercase tracking-widest text-wine-700">
              <tr>
                <th className="px-4 py-2.5 text-left">Société</th>
                <th className="px-4 py-2.5 text-left">Payée le</th>
                <th className="px-4 py-2.5 text-left">Type</th>
                <th className="px-4 py-2.5 text-right">Montant TTC</th>
                <th className="px-4 py-2.5 text-right">Commission</th>
              </tr>
            </thead>
            <tbody>
              {items.map((it) => (
                <tr key={it.invoice_id} className="border-t border-neutral-100">
                  <td className="px-4 py-2.5 font-semibold text-navy">{it.company_name}</td>
                  <td className="px-4 py-2.5 text-neutral-600">{fmtDate(it.paid_at)}</td>
                  <td className="px-4 py-2.5">
                    <span
                      className={`rounded-full px-2 py-0.5 text-[10px] font-bold ${
                        it.type === "renewal" ? "bg-amber-50 text-amber-700" : "bg-emerald-50 text-emerald-700"
                      }`}
                    >
                      {it.type === "renewal" ? "Renouvellement" : "Nouvelle adhésion"} ·{" "}
                      {Math.round(it.commission_rate * 100)}%
                    </span>
                  </td>
                  <td className="px-4 py-2.5 text-right text-neutral-700">{fmtEur(it.amount_ttc)}</td>
                  <td className="px-4 py-2.5 text-right font-semibold text-wine-700">{fmtEur(it.commission_amount)}</td>
                </tr>
              ))}
            </tbody>
            <tfoot>
              <tr className="border-t border-wine-200 bg-rose-50/40 font-bold text-navy">
                <td colSpan={3} className="px-4 py-3 text-right uppercase tracking-widest text-[11px]">Total</td>
                <td className="px-4 py-3 text-right">{fmtEur(totals.ttc_total)}</td>
                <td className="px-4 py-3 text-right text-wine-700">{fmtEur(totals.commission_total)}</td>
              </tr>
            </tfoot>
          </table>
        )}
      </div>

      <p className="mt-3 text-[11px] text-neutral-500">
        📧 Un récap mensuel branded est envoyé automatiquement à chaque commercial (et sa CC) le dernier jour de chaque mois.
        {data?.duplicates_skipped > 0 && (
          <>
            {" · "}
            <span className="font-semibold text-amber-700" data-testid="commission-dedup-note">
              {data.duplicates_skipped} doublon{data.duplicates_skipped > 1 ? "s" : ""} Stripe ignoré{data.duplicates_skipped > 1 ? "s" : ""}
            </span>
          </>
        )}
      </p>

      {data?.excluded_memberships?.length > 0 && (
        <details className="mt-4 rounded-2xl border border-amber-200 bg-amber-50/50 p-4" data-testid="commission-excluded-panel">
          <summary className="cursor-pointer text-sm font-semibold text-amber-800">
            ⚠️ {data.excluded_memberships.length} paiement{data.excluded_memberships.length > 1 ? "s" : ""} membership à vérifier ({current?.scope_label} non détecté){" "}
            <span className="font-normal text-amber-700">— cliquez pour voir et retaguer si besoin</span>
          </summary>
          <p className="mt-2 text-[11px] text-amber-700">
            Ces factures Stripe ressemblent à des adhésions mais n'ont pas été taguées <strong>{current?.scope_label}</strong> dans la fiche société/candidature. Vérifiez et re-taguez-les pour les faire remonter.
          </p>
          <table className="mt-3 w-full text-xs">
            <thead className="text-left uppercase tracking-widest text-amber-700/80">
              <tr>
                <th className="py-1.5 pr-3">Société</th>
                <th className="py-1.5 pr-3">Payée le</th>
                <th className="py-1.5 pr-3">Catégorie actuelle</th>
                <th className="py-1.5 pr-3 text-right">Montant TTC</th>
                <th className="py-1.5 pr-3">Fiche</th>
              </tr>
            </thead>
            <tbody>
              {data.excluded_memberships.map((it) => (
                <tr key={it.invoice_id} className="border-t border-amber-200/60">
                  <td className="py-1.5 pr-3 font-semibold text-navy">{it.company_name}</td>
                  <td className="py-1.5 pr-3 text-neutral-600">{fmtDate(it.paid_at)}</td>
                  <td className="py-1.5 pr-3 text-amber-800">{it.detected_category}</td>
                  <td className="py-1.5 pr-3 text-right text-neutral-700">{fmtEur(it.amount_ttc)}</td>
                  <td className="py-1.5 pr-3">
                    {it.company_id && (
                      <a href={`/admin/companies/${it.company_id}`} className="font-semibold text-wine-700 hover:underline" target="_blank" rel="noreferrer">
                        Société →
                      </a>
                    )}
                    {!it.company_id && it.application_id && (
                      <a href={`/admin/applications/${it.application_id}`} className="font-semibold text-wine-700 hover:underline" target="_blank" rel="noreferrer">
                        Candidature →
                      </a>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </details>
      )}
    </section>
  );
}

export default function AdminAnalytics() {
  const [direction, setDirection] = useState(null);
  const [eco, setEco] = useState(null);
  const [evolution, setEvolution] = useState(null);

  const reloadDirection = () => {
    api.get("/admin/direction-stats").then((r) => setDirection(r.data)).catch(() => {});
  };

  useEffect(() => {
    reloadDirection();
    api.get("/admin/stats").then((r) => setEco(r.data)).catch(() => {});
    api.get("/admin/analytics/members-evolution").then((r) => setEvolution(r.data)).catch(() => {});
  }, []);

  if (!direction || !eco) {
    return (
      <PortalLayout sidebar="admin">
        <div className="py-20 text-center text-neutral-500">Chargement...</div>
      </PortalLayout>
    );
  }

  const totalPole = (eco.pole_breakdown || []).reduce((s, p) => s + p.count, 0);
  const totalCat = (eco.category_breakdown || []).reduce((s, c) => s + c.count, 0);

  return (
    <PortalLayout sidebar="admin">
      <div className="space-y-8">
        <div>
          <h1 className="inline-flex items-center gap-2 font-serif text-4xl text-navy">
            <Sparkles className="h-7 w-7 text-wine-600" /> Analytics
          </h1>
          <p className="mt-1 text-sm text-neutral-600">
            Vue consolidée : Chiffre d&apos;affaires · Acquisition · Événements · Contenus · Écosystème.
          </p>
        </div>

        {/* Revenue */}
        <RevenueSection revenue={direction.revenue} onTargetSaved={reloadDirection} />

        {/* Audrey commission (15% new / 5% renewal on Startup) */}
        <AudreyCommissionSection />

        {/* Shared services revenue (Stripe) */}
        <SharedServicesRevenueSection />

        {/* Members evolution — new vs churn over last 12 months */}
        <MembersEvolutionSection data={evolution} />

        {/* Acquisition */}
        <section
          className={`rounded-3xl bg-gradient-to-b ${SECTION_COLOR.acquisition} p-6`}
          data-testid="section-acquisition"
        >
          <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
            <Target className="h-5 w-5 text-wine-600" /> Acquisition
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={Users} label="Contacts" value={direction.acquisition.contacts_total} hint={`+${direction.acquisition.contacts_new_30d} sur 30j`} accent="wine" />
            <Kpi icon={TrendingUp} label="Prospects gagnés" value={direction.acquisition.prospects_won} hint={`Taux conv. ${direction.acquisition.conv_rate_pct}%`} accent="wine" />
            <Kpi icon={FileText} label="Candidatures payées" value={direction.acquisition.applications_paid} hint={`/ ${direction.acquisition.applications_total} totales`} accent="wine" />
            <Kpi icon={Users} label="Membres actifs" value={direction.acquisition.members_active} hint="Statut actif" accent="wine" />
          </div>
          <div className="mt-5 rounded-2xl border border-neutral-100 bg-white p-5">
            <h3 className="inline-flex items-center gap-2 text-sm font-bold text-neutral-700">
              <BarChart3 className="h-4 w-4 text-wine-600" /> Contacts par source
            </h3>
            <div className="mt-3">
              <Bars data={direction.acquisition.by_source} labelKey="source" color="wine" />
            </div>
          </div>
        </section>

        {/* Events */}
        <section
          className={`rounded-3xl bg-gradient-to-b ${SECTION_COLOR.events} p-6`}
          data-testid="section-events"
        >
          <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
            <Calendar className="h-5 w-5 text-emerald-600" /> Événements
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={Calendar} label="À venir" value={direction.events.upcoming} hint={`${direction.events.total_published} publiés au total`} accent="emerald" />
            <Kpi icon={Clock} label="30 derniers jours" value={direction.events.past_30d} hint="événements terminés" accent="emerald" />
            <Kpi icon={Users} label="Inscriptions" value={direction.events.registrations_total} hint={`+${direction.events.registrations_30d} sur 30j`} accent="emerald" />
            <Kpi icon={TrendingUp} label="Taux de remplissage" value={`${direction.events.fill_rate_pct}%`} hint="moyenne pondérée" accent="emerald" />
          </div>
        </section>

        {/* Contents */}
        <section
          className={`rounded-3xl bg-gradient-to-b ${SECTION_COLOR.contents} p-6`}
          data-testid="section-contents"
        >
          <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
            <Megaphone className="h-5 w-5 text-indigo-600" /> Contenus
          </h2>
          <div className="mt-5 grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
            <Kpi icon={FileText} label="Actualités publiées" value={direction.contents.news_published} hint={`+${direction.contents.news_30d} sur 30j`} accent="indigo" />
            <Kpi icon={Clock} label="En attente de validation" value={direction.contents.news_pending} hint="modération" accent="amber" />
            <Kpi icon={Mail} label="Campagnes email (30j)" value={direction.contents.mailshots_campaigns_30d} hint={`${direction.contents.mailshots_sent_30d} emails envoyés`} accent="indigo" />
            <Kpi icon={Mail} label="Campagnes totales" value={direction.contents.mailshots_total} hint="historique mailshots" accent="indigo" />
          </div>
          <div className="mt-5 grid gap-4 lg:grid-cols-2">
            <div className="rounded-2xl border border-neutral-100 bg-white p-5">
              <h3 className="inline-flex items-center gap-2 text-sm font-bold text-neutral-700">
                <BarChart3 className="h-4 w-4 text-indigo-600" /> Top auteurs
              </h3>
              <div className="mt-3">
                <Bars data={direction.contents.top_authors} labelKey="author" color="indigo" />
              </div>
            </div>
            <div className="rounded-2xl border border-neutral-100 bg-white p-5">
              <h3 className="inline-flex items-center gap-2 text-sm font-bold text-neutral-700">
                <BarChart3 className="h-4 w-4 text-indigo-600" /> Actualités par catégorie
              </h3>
              <div className="mt-3">
                <Bars data={direction.contents.by_category} labelKey="category" color="indigo" />
              </div>
            </div>
          </div>
        </section>

        {/* Ecosystem — Poles / Categories */}
        <section
          className={`rounded-3xl bg-gradient-to-b ${SECTION_COLOR.ecosystem} p-6`}
          data-testid="section-ecosystem"
        >
          <h2 className="inline-flex items-center gap-2 font-serif text-2xl text-navy">
            <Tag className="h-5 w-5 text-blush-500" /> Écosystème
          </h2>
          <div className="mt-5 grid gap-6 lg:grid-cols-2">
            <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="analytics-poles-block">
              <h3 className="font-serif text-xl text-neutral-900">Répartition par pôle</h3>
              <p className="mt-0.5 text-xs text-neutral-500">
                {(eco.pole_breakdown || []).length} pôles · {totalPole} membre{totalPole > 1 ? "s" : ""}
              </p>
              <div className="mt-5 space-y-3">
                {eco.pole_breakdown.length === 0 && (
                  <p className="text-sm text-neutral-400">Aucun pôle renseigné pour le moment.</p>
                )}
                {eco.pole_breakdown.map((p) => {
                  const pct = totalPole > 0 ? Math.round((p.count / totalPole) * 100) : 0;
                  const isEmpty = !p.pole || p.pole === "—";
                  return (
                    <div key={p.pole} data-testid={`analytics-pole-${p.pole}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className={isEmpty ? "italic text-neutral-400" : "text-neutral-700"}>
                          {isEmpty ? "Non renseigné" : poleLabel(p.pole)}
                        </span>
                        <span className="font-semibold text-wine-700">{p.count} ({pct}%)</span>
                      </div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-wine-50">
                        <div
                          className={`h-full rounded-full ${isEmpty ? "bg-neutral-300" : "bg-gradient-to-r from-wine-400 to-wine-600"}`}
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>

            <div className="rounded-2xl border border-neutral-100 bg-white p-6" data-testid="analytics-categories-block">
              <h3 className="font-serif text-xl text-neutral-900">
                <Tag className="mr-1 inline h-4 w-4 text-blush-500" /> Répartition par catégorie
              </h3>
              <p className="mt-0.5 text-xs text-neutral-500">
                {(eco.category_breakdown || []).length} catégories d&apos;adhésion · {totalCat} membre{totalCat > 1 ? "s" : ""}
              </p>
              <div className="mt-5 space-y-3">
                {(eco.category_breakdown || []).length === 0 && (
                  <p className="text-sm text-neutral-400">Aucune catégorie renseignée pour le moment.</p>
                )}
                {(eco.category_breakdown || []).map((c) => {
                  const pct = totalCat > 0 ? Math.round((c.count / totalCat) * 100) : 0;
                  return (
                    <div key={c.category_id} data-testid={`analytics-category-${c.category_id}`}>
                      <div className="flex items-center justify-between text-sm">
                        <span className="text-neutral-700">{c.category}</span>
                        <span className="font-semibold text-blush-700">{c.count} ({pct}%)</span>
                      </div>
                      <div className="mt-1 h-2 overflow-hidden rounded-full bg-blush-50">
                        <div
                          className="h-full rounded-full bg-gradient-to-r from-blush-400 to-blush-600"
                          style={{ width: `${pct}%` }}
                        />
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          </div>
        </section>

        {/* Sprint 89b — Onboarding score across all members */}
        <OnboardingScoreSection />

        <p className="text-right text-xs text-neutral-400">
          Dernière mise à jour : {new Date(direction.generated_at).toLocaleString("fr")}
        </p>
      </div>
    </PortalLayout>
  );
}

function OnboardingScoreSection() {
  const [data, setData] = useState(null);
  useEffect(() => {
    api.get("/admin/analytics/onboarding-score").then((r) => setData(r.data)).catch(() => {});
  }, []);
  if (!data) return null;
  const dist = data.distribution || {};
  const steps = data.steps_completion || {};
  const total = data.total_members || 0;
  return (
    <section className="rounded-3xl bg-gradient-to-b from-amber-50 to-white p-6" data-testid="section-onboarding-score">
      <h3 className="mb-4 flex items-center gap-2 font-serif text-xl text-neutral-900">
        <Sparkles className="h-5 w-5 text-amber-500" /> Score d&apos;onboarding — Wine Founder Program
      </h3>
      <div className="grid gap-4 md:grid-cols-4">
        <div className="rounded-2xl border border-amber-200 bg-white p-5" data-testid="onboarding-avg-score">
          <div className="text-[10px] font-bold uppercase text-amber-600">Score moyen</div>
          <div className="mt-1 font-serif text-4xl font-semibold text-navy">{data.avg_score}<span className="text-lg text-neutral-500">%</span></div>
          <div className="mt-2 h-2 w-full rounded-full bg-neutral-100">
            <div className="h-2 rounded-full bg-amber-500" style={{ width: `${data.avg_score}%` }} />
          </div>
          <div className="mt-2 text-[11px] text-neutral-500">Sur {total} membres actifs</div>
        </div>
        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          <div className="text-[10px] font-bold uppercase text-neutral-500">Distribution</div>
          {["3/3", "2/3", "1/3", "0/3"].map((k) => {
            const n = dist[k] || 0;
            const pct = total ? Math.round(n * 100 / total) : 0;
            return (
              <div key={k} className="mt-2 flex items-center gap-2">
                <span className="w-8 text-xs font-bold text-neutral-700">{k}</span>
                <div className="flex-1 h-2 rounded-full bg-neutral-100">
                  <div className={`h-2 rounded-full ${k === "3/3" ? "bg-emerald-500" : k === "0/3" ? "bg-rose-400" : "bg-amber-400"}`} style={{ width: `${pct}%` }} />
                </div>
                <span className="w-10 text-right text-xs text-neutral-500">{n}</span>
              </div>
            );
          })}
        </div>
        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          <div className="text-[10px] font-bold uppercase text-neutral-500">Complétion par étape</div>
          {[
            ["profile_complete", "🏆 Profil complet"],
            ["news_published", "📣 1ère publication"],
            ["connection_made", "🤝 1ère mise en relation"],
          ].map(([k, label]) => {
            const n = steps[k] || 0;
            const pct = total ? Math.round(n * 100 / total) : 0;
            return (
              <div key={k} className="mt-2">
                <div className="flex items-center justify-between text-xs">
                  <span className="text-neutral-700">{label}</span>
                  <span className="font-bold text-navy">{n} <span className="text-neutral-400">/ {total}</span></span>
                </div>
                <div className="mt-1 h-1.5 w-full rounded-full bg-neutral-100">
                  <div className="h-1.5 rounded-full bg-wine-500" style={{ width: `${pct}%` }} />
                </div>
              </div>
            );
          })}
        </div>
        <div className="rounded-2xl border border-neutral-100 bg-white p-5">
          <div className="text-[10px] font-bold uppercase text-neutral-500">🚨 À relancer (top 5)</div>
          <ul className="mt-2 space-y-1" data-testid="onboarding-top-incomplete">
            {(data.top_incomplete || []).slice(0, 5).map((m) => (
              <li key={m.user_id} className="flex items-center justify-between text-xs">
                <span className="truncate" title={m.email}>{m.company_name || m.email}</span>
                <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${m.score === 0 ? "bg-rose-100 text-rose-700" : "bg-amber-100 text-amber-700"}`}>{m.score}%</span>
              </li>
            ))}
            {(data.top_incomplete || []).length === 0 && (
              <li className="text-xs text-emerald-700">✨ Tous les membres ont complété leur onboarding !</li>
            )}
          </ul>
        </div>
      </div>
    </section>
  );
}
